/**
 * Back Office → Timesheet: the manager side of THE time clock (rebuilt Oct 29 2026).
 *
 * - On the clock: who is working / on a break right now, who is scheduled but not in;
 *   clock someone out (with a reason).
 * - Timecards: every shift by the restaurant's local dates with its breaks, the period's
 *   hours exactly as Payroll pays them (unpaid breaks out, overtime over 8 h a day then 40 h
 *   a week); edit a shift or add a missed one — always with a reason, always audited.
 * - Needs review: missed clock-outs (flagged after the configured hours) and offline punches
 *   that arrived out of order; Payroll does not pay them until a manager fixes / approves.
 * - Settings: the missed clock-out threshold.
 *
 * All of it is labor-service /api/timeclock/v2 on time_clock_entries, the rows Payroll reads.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertTriangle, CalendarClock, CheckCircle2, ChevronLeft, ChevronRight, Clock, Coffee, ExternalLink,
  History, LogOut, Pencil, Plus, RefreshCw, Settings, Trash2, Users, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getBranchAuthHeaders } from '@/utils/branchApi';
import { ACTION_LABEL, timeClockApi } from '@/lib/timeclock/api';
import { fmtDate, fmtDuration, fmtHours, fmtTime, isoToZonedInput, zonedInputToIso, zonedToday } from '@/lib/timeclock/time';

const STATUS_BADGE = {
  active: { label: 'On the clock', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  review: { label: 'Needs review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.completed;
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

function Stat({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700', emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700', sky: 'bg-sky-100 text-sky-700',
  };
  return (
    <Card>
      <CardContent className="pt-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ dialogs

function ReasonDialog({ open, title, description, confirm = 'Save', withTime = false, tz, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(''); setTime(''); } }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(reason.trim(), time ? zonedInputToIso(time, tz) : null);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {withTime && (
          <div className="space-y-1.5">
            <Label>Clock-out time ({tz}) — empty = now</Label>
            <Input type="datetime-local" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Reason (kept with the change)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Left at close, forgot to clock out" data-testid="reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="reason-submit">{confirm}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntryDialog({ open, entry, roster, tz, api, onClose, onSaved }) {
  const isNew = !entry?.id;
  const [staffId, setStaffId] = useState('');
  const [roleId, setRoleId] = useState('none');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breaks, setBreaks] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStaffId(entry?.staff_id ? String(entry.staff_id) : '');
    setRoleId(entry?.role_id || 'none');
    setClockIn(isoToZonedInput(entry?.clock_in, tz));
    setClockOut(isoToZonedInput(entry?.clock_out, tz));
    setBreaks((entry?.breaks || []).map((b) => ({ start: isoToZonedInput(b.start_at, tz), end: isoToZonedInput(b.end_at, tz), paid: !!b.is_paid })));
    setReason('');
  }, [open, entry, tz]);

  const roles = (roster.find((r) => String(r.id) === staffId)?.roles) || [];
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        clock_in: zonedInputToIso(clockIn, tz),
        clock_out: clockOut ? zonedInputToIso(clockOut, tz) : null,
        breaks: breaks.map((b) => ({ start_at: zonedInputToIso(b.start, tz), end_at: b.end ? zonedInputToIso(b.end, tz) : null, is_paid: b.paid })),
        role_id: roleId === 'none' ? null : roleId,
        reason: reason.trim(),
      };
      if (isNew) await api.createEntry({ ...body, staff_id: Number(staffId) });
      else await api.editEntry(entry.id, body);
      toast.success(isNew ? 'Shift added' : 'Shift updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add a missed shift' : `Edit shift — ${entry?.staff_name || ''}`}</DialogTitle>
          <DialogDescription>Times are in the restaurant's time zone ({tz}). Unpaid breaks are taken out of the hours.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isNew && (
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={staffId || undefined} onValueChange={(v) => { setStaffId(v); setRoleId('none'); }}>
                <SelectTrigger data-testid="entry-staff"><SelectValue placeholder="Choose an employee" /></SelectTrigger>
                <SelectContent>
                  {roster.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {roles.length > 0 && (
            <div className="space-y-1.5">
              <Label>Job</Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Their staff role</SelectItem>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Clock in</Label>
              <Input type="datetime-local" value={clockIn} onChange={(e) => setClockIn(e.target.value)} data-testid="entry-in" />
            </div>
            <div className="space-y-1.5">
              <Label>Clock out</Label>
              <Input type="datetime-local" value={clockOut} onChange={(e) => setClockOut(e.target.value)} data-testid="entry-out" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Breaks</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setBreaks([...breaks, { start: clockIn, end: '', paid: false }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Break
              </Button>
            </div>
            {breaks.length === 0 && <p className="text-sm text-gray-500">No breaks.</p>}
            {breaks.map((b, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                <Input type="datetime-local" value={b.start} onChange={(e) => setBreaks(breaks.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                <Input type="datetime-local" value={b.end} onChange={(e) => setBreaks(breaks.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                <Button type="button" variant={b.paid ? 'default' : 'outline'} size="sm" onClick={() => setBreaks(breaks.map((x, j) => (j === i ? { ...x, paid: !x.paid } : x)))}>
                  {b.paid ? 'Paid' : 'Unpaid'}
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => setBreaks(breaks.filter((_, j) => j !== i))} aria-label="Remove break">
                  <Trash2 className="h-4 w-4 text-gray-500" />
                </Button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>Reason (required, kept in the shift's history)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Forgot to clock out; confirmed with the closing manager" data-testid="entry-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !clockIn || (isNew && (!staffId || !clockOut)) || reason.trim().length < 3} data-testid="entry-save">
            {isNew ? 'Add shift' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ open, entryId, api, tz, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !entryId) return;
    setData(null);
    api.audit(entryId).then(setData).catch((e) => toast.error(e.message));
  }, [open, entryId, api]);
  const summary = (s) => (s ? `${fmtTime(s.clock_in, tz)} – ${s.clock_out ? fmtTime(s.clock_out, tz) : 'open'} · ${fmtHours(s.hours_worked)} · ${s.status}` : '—');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Shift history</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-gray-500">Loading…</p> : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Punches</p>
              {data.punches.length === 0 ? <p className="text-sm text-gray-500">No punches (entered by a manager).</p> : (
                <ul className="space-y-1 text-sm">
                  {data.punches.map((p) => (
                    <li key={p.id} className="flex justify-between gap-3">
                      <span>{ACTION_LABEL[p.action]} · {p.source}{p.status !== 'applied' ? ` · ${p.status}` : ''}</span>
                      <span className="text-gray-500">{fmtDate(p.occurred_at, tz)} {fmtTime(p.occurred_at, tz)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Changes</p>
              {data.edits.length === 0 ? <p className="text-sm text-gray-500">Never changed.</p> : (
                <ul className="space-y-3">
                  {data.edits.map((x) => (
                    <li key={x.id} className="rounded-lg border p-3 text-sm">
                      <p className="font-medium text-gray-900">{x.action.replace('_', ' ')} by {x.editor_name || `#${x.editor_id}`} · {fmtDate(x.created_at, tz)} {fmtTime(x.created_at, tz)}</p>
                      <p className="text-gray-700 mt-1">“{x.reason}”</p>
                      {x.before?.clock_in && <p className="text-gray-500 mt-1">Before: {summary(x.before)}</p>}
                      {x.after?.clock_in && <p className="text-gray-500">After: {summary(x.after)}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ the page

export default function TimesheetPage() {
  const context = useOutletContext() || {};
  const token = context.token || localStorage.getItem('backoffice_token');
  const api = useMemo(() => timeClockApi(() => ({ ...getBranchAuthHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) })), [token]);

  const [tab, setTab] = useState('live');
  const [tz, setTz] = useState(null);
  const [roster, setRoster] = useState([]);
  const [live, setLive] = useState(null);
  const [review, setReview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [missedHours, setMissedHours] = useState('');
  const [range, setRange] = useState(null);
  const [staffFilter, setStaffFilter] = useState('all');
  const [entries, setEntries] = useState(null);
  const [cards, setCards] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [auditId, setAuditId] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);

  const loadBase = useCallback(async () => {
    try {
      const me = await api.me();
      const zone = me.restaurant.timezone;
      setTz(zone);
      setRange((r) => r || { from: zonedToday(zone, -6), to: zonedToday(zone) });
      setRoster((await api.roster()).employees || []);
      const s = await api.settings();
      setSettings(s);
      setMissedHours(String(s.missed_clock_out_hours));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [api]);

  const loadLive = useCallback(async () => {
    try { setLive(await api.live()); } catch (e) { setError(e.message); }
  }, [api]);
  const loadReview = useCallback(async () => {
    try { setReview(await api.review()); } catch (e) { setError(e.message); }
  }, [api]);
  const loadCards = useCallback(async () => {
    if (!range) return;
    try {
      const [list, tcards] = await Promise.all([
        api.entries({ from_date: range.from, to_date: range.to, staff_id: staffFilter === 'all' ? undefined : staffFilter }),
        api.timecards(range.from, range.to).catch(() => null),
      ]);
      setEntries(list.entries);
      setCards(tcards);
    } catch (e) {
      setError(e.message);
    }
  }, [api, range, staffFilter]);

  const refreshAll = useCallback(() => { loadLive(); loadReview(); loadCards(); }, [loadLive, loadReview, loadCards]);

  useEffect(() => { if (token) { loadBase(); loadLive(); loadReview(); } }, [token, loadBase, loadLive, loadReview]);
  useEffect(() => { if (token) loadCards(); }, [token, loadCards]);
  useEffect(() => {
    if (tab !== 'live') return undefined;
    const t = setInterval(loadLive, 30000);
    return () => clearInterval(t);
  }, [tab, loadLive]);

  const shiftWeek = (days) => {
    const move = (d) => {
      const x = new Date(`${d}T12:00:00Z`);
      x.setUTCDate(x.getUTCDate() + days);
      return x.toISOString().slice(0, 10);
    };
    setRange({ from: move(range.from), to: move(range.to) });
  };

  const saveSettings = async () => {
    try {
      const s = await api.saveSettings({ missed_clock_out_hours: Number(missedHours) });
      setSettings(s);
      toast.success('Time clock settings saved');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const approve = async (entry) => {
    try {
      await api.approveEntry(entry.id, 'Approved as recorded');
      toast.success('Shift approved — Payroll pays it');
      refreshAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const reviewCount = (review?.entries?.length || 0) + (review?.punches?.length || 0);
  const perEmployee = cards?.employees || [];

  const shiftRow = (e) => (
    <TableRow key={e.id} data-testid="timecard-row">
      <TableCell className="whitespace-nowrap">{fmtDate(e.clock_in, tz)}</TableCell>
      <TableCell>
        <p className="font-medium text-gray-900">{e.staff_name}</p>
        <p className="text-xs text-gray-500 capitalize">{e.role_name}</p>
      </TableCell>
      <TableCell className="whitespace-nowrap">{fmtTime(e.clock_in, tz)}</TableCell>
      <TableCell className="whitespace-nowrap">{e.clock_out ? fmtTime(e.clock_out, tz) : <span className="text-gray-400">—</span>}</TableCell>
      <TableCell className="text-sm text-gray-600 whitespace-nowrap">
        {e.unpaid_break_minutes ? `${e.unpaid_break_minutes}m unpaid` : ''}{e.unpaid_break_minutes && e.paid_break_minutes ? ' · ' : ''}{e.paid_break_minutes ? `${e.paid_break_minutes}m paid` : ''}
        {!e.unpaid_break_minutes && !e.paid_break_minutes ? '—' : ''}
      </TableCell>
      <TableCell className="font-semibold text-gray-900">{fmtHours(e.hours_worked)}</TableCell>
      <TableCell>
        <StatusBadge status={e.status} />
        {e.review_reason && <p className="text-xs text-amber-700 mt-1 max-w-[220px]">{e.review_reason}</p>}
        {e.is_paid && <p className="text-xs text-gray-500 mt-1">Paid (payroll #{e.payroll_id})</p>}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {!e.is_paid && <Button variant="ghost" size="icon" onClick={() => setEditing(e)} aria-label="Edit shift" data-testid="edit-entry"><Pencil className="h-4 w-4" /></Button>}
        {e.status === 'review' && e.clock_out && <Button variant="ghost" size="icon" onClick={() => approve(e)} aria-label="Approve"><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>}
        {e.status === 'active' && <Button variant="ghost" size="icon" onClick={() => setReasonFor({ kind: 'clockout', entry: e })} aria-label="Clock out"><LogOut className="h-4 w-4 text-rose-600" /></Button>}
        <Button variant="ghost" size="icon" onClick={() => setAuditId(e.id)} aria-label="History"><History className="h-4 w-4" /></Button>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Time Clock & Timesheet</h1>
          <p className="text-gray-500">Who is on the clock, every shift and break, and the hours Payroll pays{tz ? ` · ${tz}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <a href="/time-clock" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open the time clock</a>
          </Button>
          <Button variant="outline" onClick={refreshAll}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertTriangle className="h-4 w-4 mt-0.5" /> {error}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="live" data-testid="tab-live">On the clock</TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">Timecards</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">Needs review{reviewCount ? ` (${reviewCount})` : ''}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Users} label="Working" value={live?.working ?? '—'} tone="emerald" />
            <Stat icon={Coffee} label="On a break" value={live?.on_break ?? '—'} tone="sky" />
            <Stat icon={CalendarClock} label="Scheduled, not in" value={live?.scheduled_not_in?.length ?? '—'} tone="slate" />
            <Stat icon={AlertTriangle} label="Needs review" value={live?.needs_review ?? '—'} tone="amber" />
          </div>
          <Card>
            <CardHeader><CardTitle className="text-gray-900">On the clock now</CardTitle></CardHeader>
            <CardContent>
              {!live ? <p className="text-gray-500">Loading…</p> : live.on_clock.length === 0 ? (
                <p className="text-gray-500">Nobody is clocked in.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2" data-testid="live-list">
                  {live.on_clock.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-3 rounded-xl border p-4">
                      <div>
                        <p className="font-semibold text-gray-900">{e.staff_name}</p>
                        <p className="text-sm text-gray-500 capitalize">{e.role_name} · in since {fmtTime(e.clock_in, tz)}</p>
                        <p className="text-sm mt-1">
                          {e.state === 'ON_BREAK'
                            ? <span className="text-sky-700">On a {e.on_break?.is_paid ? 'paid' : 'unpaid'} break since {fmtTime(e.on_break?.start_at, tz)}</span>
                            : <span className="text-emerald-700">Working · {fmtDuration(e.worked_seconds)} so far</span>}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setEditing(e)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                        <Button variant="outline" size="sm" onClick={() => setReasonFor({ kind: 'clockout', entry: e })} data-testid="live-clock-out">
                          <LogOut className="h-3.5 w-3.5 mr-1" />Clock out
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {live?.scheduled_not_in?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-gray-900">Scheduled now, not clocked in</CardTitle></CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {live.scheduled_not_in.map((s) => (
                    <li key={s.shift_id} className="flex justify-between py-2 text-sm">
                      <span className="font-medium text-gray-900">{s.staff_name}{s.station ? ` · ${s.station}` : ''}</span>
                      <span className="text-gray-500">{fmtTime(s.start_at, tz)} – {fmtTime(s.end_at, tz)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cards" className="space-y-4">
          <Card>
            <CardContent className="pt-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => shiftWeek(-7)} disabled={!range} aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></Button>
                <Input type="date" value={range?.from || ''} onChange={(e) => setRange({ ...range, from: e.target.value })} className="w-40" />
                <span className="text-gray-500">to</span>
                <Input type="date" value={range?.to || ''} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-40" />
                <Button variant="outline" size="icon" onClick={() => shiftWeek(7)} disabled={!range} aria-label="Next week"><ChevronRight className="h-4 w-4" /></Button>
              </div>
              <div className="flex items-center gap-2">
                <Select value={staffFilter} onValueChange={setStaffFilter}>
                  <SelectTrigger className="w-52"><SelectValue placeholder="All employees" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    {roster.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={() => setEditing({})} data-testid="add-shift"><Plus className="h-4 w-4 mr-2" />Add shift</Button>
              </div>
            </CardContent>
          </Card>

          {cards && (
            <Card>
              <CardHeader>
                <CardTitle className="text-gray-900">Hours Payroll pays for this period</CardTitle>
                <p className="text-sm text-gray-500">
                  Completed shifts only · unpaid breaks out · overtime over {cards.rules.daily_overtime_hours} h a day, then over {cards.rules.weekly_overtime_hours} h a week, at {cards.rules.overtime_multiplier}×
                  {cards.not_payable.needs_review > 0 && <span className="text-amber-700"> · {cards.not_payable.needs_review} shift(s) need review and are not counted</span>}
                  {cards.not_payable.on_the_clock > 0 && <span> · {cards.not_payable.on_the_clock} still on the clock</span>}
                </p>
              </CardHeader>
              <CardContent>
                {perEmployee.length === 0 ? <p className="text-gray-500">No completed shifts in this period.</p> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead><TableHead>Shifts</TableHead><TableHead>Regular</TableHead>
                        <TableHead>Overtime</TableHead><TableHead>Total</TableHead><TableHead className="text-right">Estimated pay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {perEmployee.map((p) => (
                        <TableRow key={p.staff_id} data-testid="pay-row">
                          <TableCell className="font-medium text-gray-900">{p.name}</TableCell>
                          <TableCell>{p.entries_count}</TableCell>
                          <TableCell>{fmtHours(p.regular_hours)}</TableCell>
                          <TableCell className={p.overtime_hours ? 'text-amber-700 font-medium' : ''}>{fmtHours(p.overtime_hours)}</TableCell>
                          <TableCell className="font-semibold">{fmtHours(p.total_hours)}</TableCell>
                          <TableCell className="text-right">${(p.regular_pay + p.overtime_pay).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-gray-900">Shifts</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {!entries ? <p className="text-gray-500">Loading…</p> : entries.length === 0 ? (
                <p className="text-gray-500">No shifts in this period.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Day</TableHead><TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
                      <TableHead>Breaks</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>{entries.map(shiftRow)}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Shifts waiting for a manager</CardTitle>
              <p className="text-sm text-gray-500">Payroll does not pay these until the real times are set and the shift is approved.</p>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {!review ? <p className="text-gray-500">Loading…</p> : review.entries.length === 0 ? (
                <p className="text-gray-500 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Nothing to review.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Day</TableHead><TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
                      <TableHead>Breaks</TableHead><TableHead>Hours</TableHead><TableHead>Why</TableHead><TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>{review.entries.map(shiftRow)}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Punches kept for review</CardTitle>
              <p className="text-sm text-gray-500">Offline punches that arrived out of order (or could not apply). Nothing changed on the clock — apply a clock-out to its shift, or fix the shift and dismiss the punch.</p>
            </CardHeader>
            <CardContent>
              {!review ? null : review.punches.length === 0 ? <p className="text-gray-500">No punches waiting.</p> : (
                <ul className="divide-y" data-testid="review-punches">
                  {review.punches.map((p) => (
                    <li key={p.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{p.staff_name || 'Unknown employee'} · {ACTION_LABEL[p.action]}{p.action === 'BREAK_START' ? (p.break_paid ? ' (paid)' : ' (unpaid)') : ''}</p>
                        <p className="text-sm text-gray-500">Made {fmtDate(p.occurred_at, tz)} {fmtTime(p.occurred_at, tz)} · received {fmtTime(p.received_at, tz)} · {p.source}</p>
                        <p className="text-sm text-amber-700">{p.reason}</p>
                      </div>
                      <div className="flex gap-2">
                        {p.action === 'CLOCK_OUT' && p.entry_id && (
                          <Button size="sm" variant="outline" onClick={() => setReasonFor({ kind: 'apply', punch: p })}><CheckCircle2 className="h-4 w-4 mr-1" />Apply clock-out</Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setReasonFor({ kind: 'dismiss', punch: p })}><XCircle className="h-4 w-4 mr-1" />Dismiss</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><Settings className="h-5 w-5" />Time clock settings</CardTitle></CardHeader>
            <CardContent className="space-y-5 max-w-xl">
              <div className="space-y-1.5">
                <Label>Flag a missed clock-out after (hours on the clock)</Label>
                <div className="flex gap-2">
                  <Input type="number" min={2} max={24} value={missedHours} onChange={(e) => setMissedHours(e.target.value)} className="w-28" data-testid="missed-hours" />
                  <Button onClick={saveSettings} data-testid="save-settings">Save</Button>
                </div>
                <p className="text-sm text-gray-500">A shift still open after this many hours goes to Needs review; Payroll waits for the real clock-out. The employee can clock in again.</p>
              </div>
              {settings && (
                <div className="rounded-lg border bg-gray-50 p-4 text-sm text-gray-700 space-y-1">
                  <p><Clock className="inline h-4 w-4 mr-1" />Overtime: over {settings.overtime.daily_hours} h in a day, then over {settings.overtime.weekly_hours} h in a week, paid at {settings.overtime.multiplier}×.</p>
                  <p>Staff PIN: {settings.pin_max_attempts} wrong tries lock it for {settings.pin_lockout_minutes} minutes (the same lock as the POS sign-in).</p>
                  <p>Days and weeks are the restaurant's own time zone ({tz}).</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EntryDialog open={!!editing} entry={editing} roster={roster} tz={tz} api={api} onClose={() => setEditing(null)} onSaved={refreshAll} />
      <AuditDialog open={!!auditId} entryId={auditId} api={api} tz={tz} onClose={() => setAuditId(null)} />
      <ReasonDialog
        open={!!reasonFor}
        tz={tz}
        withTime={reasonFor?.kind === 'clockout'}
        title={reasonFor?.kind === 'clockout' ? `Clock out ${reasonFor?.entry?.staff_name}` : reasonFor?.kind === 'apply' ? 'Apply this clock-out to its shift' : 'Dismiss this punch'}
        description={reasonFor?.kind === 'dismiss' ? 'The punch stays in the shift history; nothing on the clock changes.' : undefined}
        confirm={reasonFor?.kind === 'clockout' ? 'Clock out' : reasonFor?.kind === 'apply' ? 'Apply' : 'Dismiss'}
        onClose={() => setReasonFor(null)}
        onSubmit={async (reason, time) => {
          if (reasonFor.kind === 'clockout') await api.clockOutEntry(reasonFor.entry.id, reason, time);
          else await api.resolvePunch(reasonFor.punch.id, reasonFor.kind === 'apply' ? 'apply' : 'dismiss', reason);
          toast.success('Saved');
          refreshAll();
        }}
      />
    </div>
  );
}
